// src/components/BookGuidePayment.js
import React, { useState, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import Navbar from "./Navbar";
import Footer from "./Footer";
import { toast } from "react-toastify";
import { useAuth } from "../context/AuthContext";
import "../styles/BookGuidePayment.css";
import emailjs from "@emailjs/browser";

const BookGuidePayment = () => {
    const { user, loggedIn, loading: authLoading } = useAuth();
    const location = useLocation();
    const navigate = useNavigate();
    const { guide } = location.state || {};

    const [selectedDate, setSelectedDate] = useState("");
    const [submitting, setSubmitting] = useState(false);
    const [acceptedTerms, setAcceptedTerms] = useState(false);
    const [showTerms, setShowTerms] = useState(false);

    const API_URL = process.env.REACT_APP_API_URL;

    useEffect(() => {
        if (!authLoading && !loggedIn) {
            toast.error("Please login to book a guide");
            navigate("/login");
        }
        if (!guide) {
            toast.error("No guide selected");
            navigate("/book-guide");
        }
    }, [authLoading, loggedIn, guide, navigate]);

    const totalCost = (guide?.baseFare || 0) + 200;
    const advanceAmount = Math.ceil(totalCost / 2); // 50% advance

    const handleConfirmBooking = async () => {
        if (!selectedDate) {
            toast.error("Please select a date for your trip");
            return;
        }

        setSubmitting(true);
        try {
            // Step 1: Create Razorpay order
            const orderRes = await fetch(`${API_URL}/create-razorpay-order`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({
                    amount: advanceAmount * 100, // in paise
                    currency: "INR",
                }),
            });
            const orderData = await orderRes.json();
            if (!orderRes.ok)
                throw new Error(orderData.error || "Failed to create payment order");

            // Step 2: Open Razorpay payment popup
            const options = {
                key:
                    process.env.REACT_APP_RAZORPAY_KEY_ID || "rzp_test_RIIF7VBY1Ms46I",
                amount: orderData.amount,
                currency: orderData.currency,
                name: "Trip Booking",
                description: `Advance payment for ${guide?.name}`,
                order_id: orderData.id,

                handler: async function (response) {
                    try {
                        const verifyRes = await fetch(`${API_URL}/verify-payment`, {
                            method: "POST",
                            headers: { "Content-Type": "application/json" },
                            body: JSON.stringify({
                                payment_id: response.razorpay_payment_id,
                                order_id: response.razorpay_order_id,
                                signature: response.razorpay_signature,
                                guideEmail: guide?.email,
                                guideName: guide?.name,
                                userEmail: user?.email,
                                userName: user?.name,
                                date: selectedDate,
                                totalCost,
                            }),
                        });

                        const verifyData = await verifyRes.json();
                        if (!verifyRes.ok)
                            throw new Error(verifyData.error || "Payment verification failed");

                        toast.success(
                            `✅ Payment successful! ₹${advanceAmount} paid as advance.`
                        );
                        navigate("/profile");

                        // Send confirmation email
                        const userSubject = "Booking Confirmation - Soulful Yatra";
                        const userMessage = `
Hello ${user.name},

✅ Your booking has been successfully confirmed!

Guide: ${guide.name}  
Guide Email: ${guide.email}  
Guide Contact: ${guide.phone || "N/A"}  
Places: ${guide.places?.join(", ") || "N/A"}  
Trip Date: ${new Date(selectedDate).toLocaleDateString()}  

Total Cost: ₹${totalCost}  
Advance Paid: ₹${advanceAmount}  
Remaining Balance: ₹${totalCost - advanceAmount}  

Payment ID: ${response.razorpay_payment_id}  
Order ID: ${response.razorpay_order_id}  

Cancellation & Refund Policy:
- 50% advance is non-refundable if cancelled less than 48 hours before trip.  
- Full refund if cancelled 7 days before trip.  
- If guide cancels, full refund will be issued.  
- Date changes allowed up to 3 days before the trip, subject to guide availability.  

Our guide will contact you soon with further trip details.  

Thanks for booking with us,  
Team Soulful Yatra
`;
                        sendEmail(user.email, user.name, userSubject, userMessage);

                        // Send notification email to guide
                        console.log(guide);
                        const guideSubject = "New Booking Received - Soulful Yatra";
                        const guideMessage = `
Hello ${guide.name},

🎉 You have a new booking from a traveler!

Traveler Name: ${user.name}  
Traveler Email: ${user.email}  
Traveler Phone: You will get phone number before 2 days of trip 

Trip Date: ${new Date(selectedDate).toLocaleDateString()}  
Total Cost: ₹${totalCost}  
Advance Paid: ₹${advanceAmount}  

Please contact the traveler to confirm details and plan the trip accordingly.

Thanks,  
Team Soulful Yatra
`;
                        sendEmail(guide.email, guide.name, guideSubject, guideMessage);


                    } catch (err) {
                        console.error(err);
                        toast.error(err.message || "Payment verification failed");
                    }
                },

                prefill: {
                    name: user?.name,
                    email: user?.email,
                    contact: user?.phone,
                },

                theme: { color: "#3399cc" },

                config: {
                    display: {
                        blocks: {
                            upi_block: {
                                name: "Pay via UPI",
                                instruments: [{ method: "upi" }],
                            },
                            wallet_block: {
                                name: "Pay via Wallet",
                                instruments: [{ method: "wallet" }],
                            },
                        },
                        sequence: ["upi_block", "wallet_block"],
                        preferences: {
                            show_default_blocks: true,
                        },
                    },
                },
            };

            const rzp = new window.Razorpay(options);
            rzp.open();
        } catch (err) {
            console.error(err);
            toast.error(err.message || "Booking failed");
        } finally {
            setSubmitting(false);
        }
    };

    useEffect(() => {
        emailjs.init("ZOyRJm9HRzFURhE8Z");
    }, []);

    const sendEmail = (email, name, subject, message) => {
        emailjs
            .send("service_vwckm3m", "template_z0yrbwk", {
                email: email,
                message: message,
                subject: subject,
            })
            .then((response) => {
                console.log("Email sent successfully:", response.status, response.text);
            })
            .catch((error) => {
                console.error("Error sending email:", error);
            });
    };

    return (
        <div className="book-guide-payment-container">
            <Navbar />
            <div className="book-guide-payment-content">
                <h2>Confirm Guide Booking</h2>
                <p>
                    <strong>Guide:</strong> {guide?.name || "N/A"}
                </p>
                <p>
                    <strong>Email:</strong> {guide?.email || "N/A"}
                </p>
                <p>
                    <strong>Rating:</strong> {guide?.rating?.toFixed(1) == 0 ? "No ratings yet" : guide?.rating?.toFixed(1)} ⭐
                </p>
                <p>
                    <strong>Places:</strong>{" "}
                    {guide?.places?.length ? guide.places.join(", ") : "N/A"}
                </p>
                <p>
                    <strong>Base Fare:</strong> ₹{guide?.baseFare || 0}
                </p>
                <p>
                    <strong>Extra Charges:</strong> ₹200
                </p>
                <p>
                    <strong>Total Cost:</strong> ₹{totalCost}
                </p>
                <p>
                    <strong>Advance Payment (50%):</strong> ₹{advanceAmount}
                </p>

                <label>Select Trip Date:</label>
                {guide.availableDates.length > 0 ? (
                    <select
                        value={selectedDate}
                        onChange={(e) => setSelectedDate(e.target.value)}
                    >
                        <option value="">-- Select a date --</option>
                        {guide.availableDates.map((d) => (
                            <option key={d} value={d}>
                                {new Date(d).toLocaleDateString()}
                            </option>
                        ))}
                    </select>
                ) : (
                    <p style={{ color: "red" }}>❌ No available dates for this guide</p>
                )}

                {/* Terms & Conditions */}
                <label className="terms-container">
                    <input
                        type="checkbox"
                        checked={acceptedTerms}
                        onChange={(e) => setAcceptedTerms(e.target.checked)}
                    />
                    I agree to the{" "}
                    <span
                        onClick={() => setShowTerms(true)}
                        style={{
                            color: "blue",
                            cursor: "pointer",
                            textDecoration: "underline",
                        }}
                    >
                        Terms & Conditions
                    </span>
                </label>

                <button
                    className="btn-confirm-booking"
                    onClick={handleConfirmBooking}
                    disabled={
                        submitting || guide.availableDates.length === 0 || !acceptedTerms
                    }
                >
                    {submitting ? "Processing..." : "Pay & Confirm Booking"}
                </button>
            </div>

            {/* Terms Modal */}
            {showTerms && (
                <div className="terms-modal">
                    <div className="terms-content">
                        <h3>Cancellation & Refund Policy</h3>
                        <ul>
                            <li>
                                50% advance payment is non-refundable if cancelled less than 48
                                hours before trip.
                            </li>
                            <li>Full refund if cancellation is made 7 days before trip.</li>
                            <li>If guide cancels, full advance will be refunded.</li>
                            <li>
                                Date changes are allowed up to 3 days before the trip, subject
                                to guide availability.
                            </li>
                        </ul>
                        <button onClick={() => setShowTerms(false)}>Close</button>
                    </div>
                </div>
            )}

            <Footer />
        </div>
    );
};

export default BookGuidePayment;
